import { useState } from 'react'
import cx from 'classnames'
import Errors from 'configs/errors'
import { useStores } from 'hooks/useStores'
import Logo from 'images/logo.png'
import { useFormContext, useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { Form } from 'reactstrap'
import { VStack, Text, FormControl, HStack } from '@chakra-ui/react'

import Button from 'components/Button'
import Icon from 'components/Icon'
import AuthLayout from 'components/Layouts/AuthLayout'
import Separator from 'components/Separator'
import TextField from 'components/TextField'
import routes from 'routes'
import styles from './styles.module.scss'

interface IDomainForm {
  subdomain: string
}

type DomainFormProp = {
  onAccessOrganization?: SubmitHandler<IDomainForm>
  submitting?: boolean
}

const DomainForm = ({ onAccessOrganization = () => {}, submitting = false }: DomainFormProp) => {
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useFormContext<IDomainForm>()
  return (
    <Form onSubmit={handleSubmit(onAccessOrganization)} border="none" autoComplete="off">
      <VStack 
        w="min(400px, calc(100vw - 60px))" 
        bg="white" 
        border="none" 
        gap="40px"
        align="stretch"
        alignItems="flex-start"
      >
        <Text as="h3" fontSize="lg" fontWeight="600" color="gray.700" textAlign="center" m={0}>
          Sign in to your organization
        </Text>
        
        <VStack gap="24px" align="stretch" w="100%" alignItems="flex-start">
          <FormControl>
            <VStack gap="8px" align="flex-start">
              <Text fontSize="md" fontWeight="500" color="gray.700" p={0} m={0}>
                Organization URL
              </Text>
              <HStack align="center" w="100%">
                <TextField
                  className={styles.inputOrg}
                  type="text"
                  inputMode="url"
                  data-bwignore="true"
                  data-lpignore="true"
                  data-1p-ignore
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="off"
                  {...register('subdomain', {
                    required: {
                      value: true,
                      message: Errors.ORG_REQUIRED
                    }
                  })}
                  onChange={(event: { currentTarget: { value: string } }) => setValue('subdomain', event.currentTarget.value)}
                  placeholder="your-organization-url"
                  error={!!errors.subdomain}
                />
                <Text fontWeight="bold" m={0}>.howfactory.com</Text>
              </HStack>
              <Text 
                fontSize="sm" 
                color="red.600" 
                visibility={errors.subdomain ? 'visible' : 'hidden'} 
                m={0}
                p={0}
              >
                {errors.subdomain?.message}
              </Text>
            </VStack>
          </FormControl>
          
          <Button
            type="submit"
            disabled={submitting}
            style={{ padding: '0px', margin: '0px' }}
            size="lg"
            className={styles.submitBtn}
            color="secondary"
            onClick={handleSubmit(onAccessOrganization)}
          >
            Continue&nbsp;&nbsp;&nbsp;
            <Icon icon="arrow-thin-right" group="dripicon" style={{ fontSize: 18 }} />
          </Button>
          
          <Text fontWeight="bold" textAlign="center" m={0}>
            Don't know your organization URL? <Link to="/" style={{ color: '#00A9EB', textDecoration: 'none' }}>Find now</Link>
          </Text>
          
          <Separator text="or" className={cx(styles.centerItem, styles.greySep)} style={{ width: '100%' }} />
          
          <Button outline size="lg" className={styles.registerBtn} style={{ padding: '0px', margin: '0px' }} color="light" onClick={() => navigate(routes.signUp.value)}>
            Create a new organization
          </Button>
        </VStack>
      </VStack>
    </Form>
  )
}

const DomainPage = () => {
  const [submitting, setSubmitting] = useState(false)
  const formMethods = useForm<IDomainForm>()
  const { organizationStore } = useStores()
  const mainHost = window.location.host.replace(/^(www\.)/, '').replace(/^(app\.)/, '')

  const handleAccessOrganization = (data: IDomainForm) => {
    const { subdomain } = data
    if (subdomain) {
      setSubmitting(true)
      formMethods.clearErrors()
      organizationStore
        .getOrganizationBySubdomain(subdomain)
        .then(org => {
          if (org) {
            // TODO: later when logout available will need to persist login state
            // as long as token not expired, and logout will remove the credential
            // and force to login screen
            // also need to have a wrapping mechanism to do this automatically
            // every time access a subdmain page different than public route
            const subHost = `${window.location.protocol}//${subdomain}.${mainHost}/`
            window.location.href = subHost
          }
        })
        .catch(err => {
          formMethods.setError('subdomain', { message: err.message })
          setSubmitting(false)
        })
    }
  }

  return (
    <FormProvider {...formMethods}>
      <AuthLayout
        title="Welcome to HowFactory"
        features={['All you need in one single platform', 'Fast and easy to use', 'No credit card commitment']}
      >
        <DomainForm onAccessOrganization={handleAccessOrganization} submitting={submitting} />
      </AuthLayout>
    </FormProvider>
  )
}

export default DomainPage
